import { useRef, useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'
import { CARD_ICON_COMPONENTS } from '@chillout/ui'
import type { CardIconKey } from '@chillout/ui'
import { CARD_ICON_GROUPS } from './cardIcons'

interface IconPickerFieldProps {
  value: CardIconKey
  accessibilityLabel: string
  onValueChange: (value: CardIconKey) => void
}

interface PanelPosition {
  left: number
  width: number
  maxHeight: number
  top?: number
  bottom?: number
}

const PANEL_WIDTH = 288
const PANEL_MAX_HEIGHT = 320
const PANEL_MARGIN = 8
const PANEL_MIN_HEIGHT = 120

export function IconPickerField({ value, accessibilityLabel, onValueChange }: IconPickerFieldProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const triggerRef = useRef<View>(null)
  const SelectedIcon = CARD_ICON_COMPONENTS[value]

  function handleOpen() {
    if (triggerRef.current) {
      const el = triggerRef.current as unknown as HTMLElement
      const rect = el.getBoundingClientRect()
      const width = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2)
      const left = Math.min(Math.max(rect.left, PANEL_MARGIN), window.innerWidth - PANEL_MARGIN - width)
      const spaceBelow = window.innerHeight - rect.bottom - PANEL_MARGIN
      const spaceAbove = rect.top - PANEL_MARGIN
      const openAbove = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove > spaceBelow
      const maxHeight = Math.max(Math.min(PANEL_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow), PANEL_MIN_HEIGHT)
      setPanelPos({
        left,
        width,
        maxHeight,
        ...(openAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      })
    }
    setIsOpen(true)
  }

  function handleSelect(iconKey: CardIconKey) {
    onValueChange(iconKey)
    setIsOpen(false)
  }

  const portal =
    isOpen && panelPos
      ? createPortal(
          <>
            <Pressable
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onPress={() => setIsOpen(false)}
              accessibilityLabel="Close"
            />
            <View
              className="overflow-hidden rounded-md border border-border bg-canvas-card"
              style={{
                position: 'fixed' as 'absolute',
                top: panelPos.top,
                bottom: panelPos.bottom,
                left: panelPos.left,
                width: panelPos.width,
                zIndex: 9999,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              }}
            >
              <ScrollView style={{ maxHeight: panelPos.maxHeight }} bounces={false} contentContainerClassName="gap-4 p-3">
                {CARD_ICON_GROUPS.map((group) => (
                  <View key={group.labelKey} className="gap-2">
                    <Text className="text-xs font-semibold uppercase tracking-wide text-secondary">
                      {t(group.labelKey)}
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {group.icons.map((iconKey) => {
                        const Icon = CARD_ICON_COMPONENTS[iconKey]
                        const selected = iconKey === value
                        return (
                          <Pressable
                            key={iconKey}
                            onPress={() => handleSelect(iconKey)}
                            accessibilityRole="menuitem"
                            accessibilityState={{ selected }}
                            className={`h-10 w-10 items-center justify-center rounded-md border ${
                              selected ? 'border-accent bg-accent/10' : 'border-border'
                            }`}
                          >
                            <Icon size={18} color={selected ? colors.accent.DEFAULT : colors.text.secondary} strokeWidth={1.8} />
                          </Pressable>
                        )
                      })}
                    </View>
                  </View>
                ))}
              </ScrollView>
            </View>
          </>,
          document.body,
        )
      : null

  return (
    <View>
      <Pressable
        ref={triggerRef}
        onPress={handleOpen}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded: isOpen }}
        className="h-14 xl:h-12 w-full items-center justify-center rounded-md border border-border bg-canvas-card"
      >
        <SelectedIcon size={20} color={colors.text.secondary} strokeWidth={1.5} />
      </Pressable>
      {portal}
    </View>
  )
}